import type { UserRole } from '@/types';

/**
 * The public questionnaire link (#125): `https://namat.org.sa/q`.
 * The team copies it and shares it themselves (the system sends nothing);
 * like the clinic link, a signed-out visitor signs in first and comes back.
 */
export const QUESTIONNAIRE_LINK_PATH = '/q';

/** Query flag that asks the beneficiary dashboard to open «مقياس نمط». */
export const OPEN_SCALE_PARAM = 'scale';
export const OPEN_SCALE_VALUE = 'open';

export const CUSTOMER_DASHBOARD_PATH = '/dashboard/customer';

/** Same order the sign-in uses to pick a dashboard (AuthContext). */
const ROLE_PRIORITY: UserRole[] = [
  'admin',
  'examiner',
  'manager',
  'coordinator',
  'marketer',
  'audit',
  'partner',
  'volunteer',
  'coach',
  'customer',
];

export function questionnaireLinkUrl(origin: string): string {
  return `${origin.replace(/\/+$/, '')}${QUESTIONNAIRE_LINK_PATH}`;
}

export type QuestionnaireLinkStep =
  | { kind: 'wait' }
  | { kind: 'login'; redirectAfterLogin: string }
  | { kind: 'navigate'; to: string };

export interface QuestionnaireLinkState {
  /** Auth is still resolving. */
  loading: boolean;
  signedIn: boolean;
  /** Profile not read yet while signed in. */
  profileLoaded: boolean;
  activeRoles: UserRole[];
}

/** Where `/q` sends the visitor. */
export function resolveQuestionnaireLink(state: QuestionnaireLinkState): QuestionnaireLinkStep {
  if (state.loading) return { kind: 'wait' };
  if (!state.signedIn) return { kind: 'login', redirectAfterLogin: QUESTIONNAIRE_LINK_PATH };
  if (!state.profileLoaded) return { kind: 'wait' };
  if (state.activeRoles.includes('customer')) {
    return { kind: 'navigate', to: `${CUSTOMER_DASHBOARD_PATH}?${OPEN_SCALE_PARAM}=${OPEN_SCALE_VALUE}` };
  }
  const highest = ROLE_PRIORITY.find((role) => state.activeRoles.includes(role));
  if (!highest) return { kind: 'navigate', to: '/' };
  return { kind: 'navigate', to: `/dashboard/${highest === 'coach' ? 'doctor' : highest}` };
}

/** True when the dashboard URL asks to open the «مقياس نمط» window. */
export function wantsScaleOpen(search: string): boolean {
  return new URLSearchParams(search).get(OPEN_SCALE_PARAM) === OPEN_SCALE_VALUE;
}

/** The same search string without the open flag (so a refresh does not reopen it). */
export function withoutScaleParam(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(OPEN_SCALE_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
